import { Button, Component, instantiate, Node, PageView, Prefab, warn, _decorator } from 'cc';
import { DEV } from 'cc/env';
import Lobby from '../../../../System/Lobby';
import BaseBanner from './BaseBanner';

const { ccclass, property, requireComponent } = _decorator;

@ccclass('BannerContainer')
@requireComponent(PageView)
export default class BannerContainer extends Component {
    @property({ tooltip: DEV && '是否啟用自動翻頁' })
    public enableAutoPageTurning = true;

    @property({
        tooltip: DEV && '自動翻頁間隔時間(毫秒)',
        visible(this: BannerContainer) {
            return this.enableAutoPageTurning;
        },
    })
    private readonly autoPageTurningInterval: number = 0;

    @property(Button)
    private readonly previousPageButton: Button = null;

    @property(Button)
    private readonly nextPageButton: Button = null;

    @property(Prefab)
    private readonly imageBannerPrefab: Prefab = null;

    @property(Prefab)
    private readonly youtubePlayerBannerPrefab: Prefab = null;

    private _pageView: PageView = null;

    private readonly _bannerMap: Map<Node, BaseBanner> = new Map();
    private _initalized = false;
    private _interactable = true;
    private _autoPageTuringCounter = 0;

    public get interactable() {
        return this._interactable;
    }
    public set interactable(value: boolean) {
        if (this._interactable === value) {
            return;
        }
        this._interactable = value;
        this._updateBannerInteractableState();
    }

    private get pageView() {
        if (!this._pageView) {
            this._pageView = this.getComponent(PageView);
        }
        return this._pageView;
    }

    public onLoad() {
        this.pageView.node.on(PageView.EventType.SCROLL_BEGAN, this._updateBannerInteractableState, this);
        this.pageView.node.on(PageView.EventType.SCROLLING, this._updateBannerInteractableState, this);
        this.pageView.node.on(PageView.EventType.SCROLL_ENDED, this._updateBannerInteractableState, this);
        this.pageView.node.on(PageView.EventType.PAGE_TURNING, () => {
            this._autoPageTuringCounter = 0;
            this._updateBannerCanPlayState();
        });
        this.previousPageButton.node.on(Button.EventType.CLICK, () => this._next(false), this);
        this.nextPageButton.node.on(Button.EventType.CLICK, () => this._next(), this);

        // add pages
        Lobby.hall.bannerConfigs.forEach((bannerConfig) => {
            let prefab: Prefab = null;
            switch (bannerConfig.Type) {
                case 'image':
                case 'entry':
                    prefab = this.imageBannerPrefab;
                    break;
                case 'youtube':
                    prefab = this.youtubePlayerBannerPrefab;
                    break;
                default:
                    warn(`Unknown banner type: ${bannerConfig.Type}`);
                    break;
            }
            if (prefab) {
                const bannerNode = instantiate(prefab);
                const banner = bannerNode.getComponent('BaseBanner') as BaseBanner;
                this.pageView.addPage(bannerNode);
                banner.display(bannerConfig);
                bannerNode.active = false;
                this._bannerMap.set(bannerNode, banner);
            } else {
                warn(`Unknown banner type: ${bannerConfig.Type}`);
            }
        });

        if (Lobby.hall.bannerConfigs.length <= 1) {
            this.previousPageButton.node.active = false;
            this.nextPageButton.node.active = false;
        }
    }

    public update(dt: number) {
        if (!this.enableAutoPageTurning) {
            return;
        }
        // reset once scrolling by manual
        if (this.pageView.isScrolling() && !this.pageView.isAutoScrolling()) {
            this._autoPageTuringCounter = 0;
            return;
        }
        this._autoPageTuringCounter += dt;
        const currentPage = this.pageView.getCurrentPage();
        const banner = this._bannerMap.get(currentPage);
        // do not auto turn page when video is playing
        if (banner.blockTurning) {
            this._autoPageTuringCounter = 0;
            return;
        }
        if (this._autoPageTuringCounter >= this.autoPageTurningInterval / 1000) {
            this._autoPageTuringCounter = 0;
            this._next();
        }
    }

    public onDisable(): void {
        if (Lobby.hall.bannerConfigs.length > 1) {
            this.pageView.scrollToPage(this.pageView.getCurrentPageIndex(), 0);
        }
    }

    public init(): void {
        this._initalized = true;
        this._updateBannerCanPlayState();
        this._updateBannerInteractableState();
    }

    private _updateBannerInteractableState(): void {
        if (!this._initalized) {
            return;
        }
        this.pageView.getPages().forEach((page) => {
            const banner = this._bannerMap.get(page);
            if (banner) {
                banner.interactable = this.interactable && !this.pageView.isScrolling() && page === this.pageView.getCurrentPage();
            }
        });
    }

    private _updateBannerCanPlayState(): void {
        if (!this._initalized) {
            return;
        }
        this.pageView.getPages().forEach((page) => {
            const banner = this._bannerMap.get(page);
            if (banner) {
                banner.canPlay = page === this.pageView.getCurrentPage();
            }
        });
    }

    private _next(flipRight: boolean = true): void {
        const totalPageCount = this.pageView.getPages().length;
        let nextPageIndex = (this.pageView.getCurrentPageIndex() + (flipRight ? 1 : -1)) % totalPageCount;
        if (nextPageIndex < 0) {
            nextPageIndex += totalPageCount;
        }
        this.pageView.scrollToPage(nextPageIndex);
    }
}
